import {useSettingsDialog} from "../../hooks";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Container,
    Grid2 as Grid,
    FormControl,
    Button, FormGroup, FormControlLabel, Checkbox, FormLabel, RadioGroup, Radio, TextField,
    InputAdornment, IconButton
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {COLOR_THEMES, LOCALES} from "../../constants";
import {useAtom} from "jotai";
import {colorThemeAtom, insightsEnabledAtom, localeAtom} from "../../jotai/atoms";
import {pushAnalytics} from "../../helpers";
import {getKeycloakService} from "../../services/keycloak";

export function DialogsSettings() {
    const {t, i18n} = useTranslation();

    const [locale, setLocale] = useAtom(localeAtom);
    const [colorTheme, setColorTheme] = useAtom(colorThemeAtom);
    const [insightsEnabled, setInsightsEnabled] = useAtom(insightsEnabledAtom);

    const {isOpen, close} = useSettingsDialog();

    const [authToken, setAuthToken] = useState("");
    const [iAmToken, setIAmToken] = useState("");
    const [orgId, setOrgId] = useState("");
    const [showAuthToken, setShowAuthToken] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Check if user is authenticated via Keycloak
            const keycloakService = getKeycloakService();
            const isKeycloakAuthenticated = keycloakService && keycloakService.isAuthenticated();
            
            // Only show tokens if user is authenticated via Keycloak
            if (isKeycloakAuthenticated) {
                setAuthToken(localStorage.getItem("authToken") || "");
            } else {
                setAuthToken(""); // Don't show token if not authenticated
            }
            
            setIAmToken(localStorage.getItem("iAmToken") || "");
            setOrgId(localStorage.getItem("orgId") || "");
        }
    }, [isOpen]);

    const handleAuthTokenChange = (event) => {
        const value = event.target.value;
        setAuthToken(value);
        if (value) {
            localStorage.setItem("authToken", value);
        } else {
            localStorage.removeItem("authToken");
        }
    };

    const handleIAmTokenChange = (event) => {
        const value = event.target.value;
        setIAmToken(value);
        if (value) {
            localStorage.setItem("iAmToken", value);
        } else {
            localStorage.removeItem("iAmToken");
        }
    };

    const handleOrgIdChange = (event) => {
        const value = event.target.value;
        setOrgId(value);
        if (value) {
            localStorage.setItem("orgId", value);
        } else {
            localStorage.removeItem("orgId");
        }
    };

    const handleLocaleChange = event => {
        const nextLocale = event.target.value;

        pushAnalytics('localeSelected', { locale: nextLocale });

        i18n.changeLanguage(nextLocale);
        setLocale(nextLocale);
    };

    const handleColorThemeChange = event => {
        const nextColorTheme = event.target.value;

        pushAnalytics('colorThemeSelected', { colorTheme: nextColorTheme });

        setColorTheme(nextColorTheme);
    };

    return <Dialog onClose={close} open={isOpen} maxWidth="sm" fullWidth>
        <DialogTitle>{t('components:dialogs.settings.title')}</DialogTitle>
        <DialogContent>
            <Container component="main" sx={{mt: 2, mb: 2}} maxWidth={false}>
                <Grid container spacing={2}>
                    <Grid size={{xs: 12}}>
                        <FormControl fullWidth>
                            <FormLabel>{t('components:dialogs.settings.fields.locale.label')}</FormLabel>
                            <RadioGroup
                                row
                                name="locale"
                                value={locale}
                                onChange={handleLocaleChange}
                            >
                                {LOCALES.map(l => <FormControlLabel value={l} control={<Radio/>}
                                                                    label={t(`common:language.${l}`)}
                                                                    key={`locale-${l}`}/>)}
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <FormControl fullWidth>
                            <FormLabel>{t('components:dialogs.settings.fields.color_theme.label')}</FormLabel>
                            <RadioGroup
                                row
                                name="color-theme"
                                value={colorTheme}
                                onChange={handleColorThemeChange}
                            >
                                {COLOR_THEMES.map(theme => <FormControlLabel value={theme} control={<Radio/>}
                                                                             label={t(`common:color_theme.${theme}`)}
                                                                             key={`color-theme-${theme}`}/>)}
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox/>}
                                              label={t('components:dialogs.settings.fields.insights_enabled.label')}
                                              checked={insightsEnabled}
                                              onChange={(e) => { setInsightsEnabled(e.target.checked); pushAnalytics(e.target.checked ? 'insightsEnabled' : 'insightsDisabled') }}/>
                        </FormGroup>
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <FormControl fullWidth>
                            <FormLabel sx={{mb: 1}}>Параметры авторизации Яндекс</FormLabel>
                        </FormControl>
                    </Grid>
                    <Grid size={{xs: 12}}>
                        {(() => {
                            const keycloakService = getKeycloakService();
                            const isKeycloakAuthenticated = keycloakService && keycloakService.isAuthenticated();
                            
                            // Only show token field if user is authenticated via Keycloak
                            if (!isKeycloakAuthenticated) {
                                return (
                                    <TextField
                                        fullWidth
                                        label="OAuth Token (Auth Token)"
                                        variant="outlined"
                                        value=""
                                        disabled
                                        helperText="Для просмотра токена необходимо авторизоваться"
                                    />
                                );
                            }
                            
                            return (
                                <TextField
                                    fullWidth
                                    label="OAuth Token (Auth Token)"
                                    variant="outlined"
                                    value={authToken}
                                    onChange={handleAuthTokenChange}
                                    type={showAuthToken ? "text" : "password"}
                                    helperText="OAuth токен от Яндекс ID"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowAuthToken(!showAuthToken)}
                                                    edge="end"
                                                    aria-label="toggle password visibility"
                                                >
                                                    {showAuthToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            );
                        })()}
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <TextField
                            fullWidth
                            label="IAM Token"
                            variant="outlined"
                            value={iAmToken}
                            onChange={handleIAmTokenChange}
                            type="password"
                            helperText="IAM токен от Яндекс.Облака"
                        />
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <TextField
                            fullWidth
                            label="Organization ID"
                            variant="outlined"
                            value={orgId}
                            onChange={handleOrgIdChange}
                            helperText="ID организации в Яндекс.Трекере"
                        />
                    </Grid>
                    <Grid size={{xs: 12}}>
                        <FormControl fullWidth>
                            <Button variant="contained" onClick={close}>{t('common:button.close')}</Button>
                        </FormControl>
                    </Grid>
                </Grid>
            </Container>

        </DialogContent>
    </Dialog>
}